/**
 * What the live data says about one coupon code, and therefore what should
 * happen to it.
 */
import { TCouponDomainSource } from "./coupon-issues";

/**
 * failing: nobody got a discount. rarely: it worked, but on fewer than the
 * chosen share of checked attempts. working: everything else.
 */
export type TCouponVerdict = "working" | "rarely" | "failing";

/** Default share of checked attempts a code must win to count as working. */
export const COUPON_WORKING_MIN_RATE = 0.99;
/** How a failed attempt with a blank ErrorMessage is labelled. */
export const COUPON_NO_MESSAGE = "(no message)";

/** Below this many checked attempts a code that worked is never "rarely". */
export const COUPON_RARELY_MIN_CHECKED = 10;

/** One partner's slice of a row's traffic. */
export interface ICouponPartnerShare {
  partnerId: string;
  name: string | null;
  attempts: number;
  applied: number;
}

export interface ICouponPerformanceRow {
  code: string;
  brandId: string | null;
  brandName: string | null;
  /** The deal the code ran under — one of possibly several in the window. */
  campaignId: string | null;

  attempts: number;
  checkouts: number;
  applied: number;
  /** Attempts that never found a coupon box — our template, not the code. */
  templateBlocked: number;
  /** applied / (attempts − templateBlocked). Null when nothing was checked. */
  applyRate: number | null;

  totalSaved: number;
  /** A display symbol as the extension scraped it, not an ISO code. */
  currency: string | null;
  firstSeen: string;
  lastSeen: string;
  lastError: string | null;

  /**
   * The merchant's site: the checkout URL a shopper reported, else the
   * coupon's, deal's or brand's own domain. Null when none of those names
   * exactly one site, so a guess never sends someone to the wrong merchant.
   */
  domain: string | null;
  domainSource: TCouponDomainSource | null;
  /** Why there is no domain, e.g. "brand has 14 domains". */
  domainNote: string | null;
  /** The markets (geos) the coupon is enabled in, Kindred spelling ("UK"). */
  markets: string[];

  /**
   * Where the shoppers trying this code were, busiest first.
   *
   * This is the SHOPPER's country as the extension reported it, not the
   * merchant's geo — a code can be tried from anywhere. Useful for spotting a
   * code that only works in the market it was issued for.
   */
  countries: Array<{ country: string; attempts: number; applied: number }>;
  /** Which partner apps the attempts came from, busiest first. */
  partners: ICouponPartnerShare[];
  /**
   * The exact checkout page an attempt happened on, where one was reported.
   * Only about 1 event in 90 carries it — Android sends none — so it is a
   * bonus on the rows that have it, never the column the page relies on.
   */
  checkoutUrl: string | null;

  /** Our own record, when the code resolves to one. */
  couponId: string | null;
  publishedStatus: number | null;
  validTo: string | null;
  lastTestedAt: string | null;
  isLive: boolean;

  verdict: TCouponVerdict;
  /** Dead AND still served — the list the team should act on. */
  deactivationCandidate: boolean;
}

export interface ICouponPerformanceResponse {
  items: ICouponPerformanceRow[];
  total: number;
  offset: number;
  limit: number;
  counts: Record<TCouponVerdict, number>;
  /** Codes that are dead in the wild and still being served. */
  deactivationCandidates: number;
  window: { from: string; to: string };
  /** What the window held, so the page can state its own limits. */
  coverage: {
    rows: number;
    events: number;
    distinctCodes: number;
    rowsWithCode: number;
    rowsWithBrandId: number;
    eventsWithDealUrl: number;
  };
}

/** A partner app shipping the extension, cached from the partners DB. */
export interface ICouponPartner {
  partnerId: string;
  name: string;
}

export interface ICouponPartnersResponse {
  items: ICouponPartner[];
  cachedAt: string;
}

/**
 * The same window counted two ways. An attempt is one code submitted; a
 * checkout is one extension event (EventId), which tries several codes.
 */
export interface ICouponRateCounts {
  attempts: number;
  /** Attempts the shop accepted. */
  applied: number;
  /** Attempts that never found a coupon box — our template, not the code. */
  templateBlocked: number;
  checkouts: number;
  /** Checkouts where at least one code applied. */
  checkoutsWithApply: number;
  /** Checkouts where every attempt was template-blocked. */
  checkoutsAllBlocked: number;
}

export interface ICouponInsightsTotals extends ICouponRateCounts {
  /** Distinct code + brand pairs tried. */
  codes: number;
  /** Of those, how many applied at least once. */
  workingCodes: number;
  /** Of the working ones, how many fall under the minimum success rate. */
  rarelyCodes: number;
  /** Rows with no coupon code at all, left out of every count above. */
  rowsWithoutCode: number;
}

/** Short windows are bucketed by hour, longer ones by UTC day. */
export type TCouponInsightsBucket = "hour" | "day";

export interface ICouponInsightsPeriod extends ICouponRateCounts {
  /** "2026-10-13T04:00:00Z" for an hour, "2026-10-13" for a UTC day. */
  period: string;
  codes: number;
  workingCodes: number;
}

export interface ICouponInsightsCheckoutSize {
  /** Codes tried in one checkout: "1", "2", "3", "4-5", "6-10", "11+". */
  bucket: string;
  checkouts: number;
  checkoutsWithApply: number;
  attempts: number;
  applied: number;
}

export interface ICouponInsightsPartnerRow extends ICouponRateCounts {
  partnerId: string;
  name: string | null;
}

export interface ICouponInsightsPlatformRow extends ICouponRateCounts {
  platform: string;
}

export interface ICouponInsightsCountryRow extends ICouponRateCounts {
  country: string;
}

export interface ICouponInsightsBrandRow extends ICouponRateCounts {
  brandId: string | null;
  brandName: string | null;
  partners: ICouponPartnerShare[];
}

export interface ICouponInsightsErrorRow {
  message: string;
  attempts: number;
  codes: number;
  brands: number;
  lastSeen: string;
  partners: ICouponPartnerShare[];
}

export interface ICouponInsightsCodeRow {
  code: string;
  brandId: string | null;
  brandName: string | null;
  attempts: number;
  applied: number;
  templateBlocked: number;
  checkouts: number;
  lastError: string | null;
  partners: ICouponPartnerShare[];
}

export interface ICouponInsightsResponse {
  window: { from: string; to: string };
  partnerId: string | null;
  minRate: number;
  totals: ICouponInsightsTotals;
  bucket: TCouponInsightsBucket;
  timeline: ICouponInsightsPeriod[];
  checkoutSizes: ICouponInsightsCheckoutSize[];
  byPartner: ICouponInsightsPartnerRow[];
  byPlatform: ICouponInsightsPlatformRow[];
  byCountry: ICouponInsightsCountryRow[];
  /** Brands with the most checkouts that got no discount. */
  topFailingBrands: ICouponInsightsBrandRow[];
  /** Codes with the most rejected attempts. */
  topFailingCodes: ICouponInsightsCodeRow[];
  /** What the shops said, most frequent first. */
  topErrors: ICouponInsightsErrorRow[];
}

/** One CouponAnalytics row, as written by the extension. */
export interface ICouponAnalyticsEventRow {
  id: string;
  eventId: string;
  timeStamp: string;
  partnerId: string;
  partnerName: string | null;
  platform: string;
  country: string | null;
  brandId: string | null;
  campaignId: string | null;
  code: string | null;
  status: string;
  errorMessage: string | null;
  isCheckoutCoupon: boolean;
  amountSaved: number;
  basketValue: number;
  currency: string | null;
  dealUrl: string | null;
}

export interface ICouponAnalyticsEventsResponse {
  items: ICouponAnalyticsEventRow[];
  total: number;
  offset: number;
  limit: number;
}

/** Progress of the CouponAnalytics → Mongo mirror that every tab reads. */
export interface ICouponAnalyticsSyncState {
  status: "idle" | "pending" | "running" | "failed";
  full: boolean;
  requestedAt: string | null;
  startedAt: string | null;
  heartbeatAt: string | null;
  finishedAt: string | null;
  /** Rows with a TimeStamp before this were in SQL when the last sync read them. */
  syncedThrough: string | null;
  /** Rows read by the current or last run. */
  runRows: number;
  /** Time the run spent reading SQL and writing Mongo; they overlap, so they need not add up. */
  timing: { sqlMs: number; writeMs: number };
  totalRows: number;
  error: string | null;
  /** The last run's row-count check against SQL. */
  /** `error` is set when the check itself failed; the copy still counts. */
  verify: {
    from: string;
    sqlRows: number | null;
    mongoRows: number | null;
    error: string | null;
  } | null;
}
